import { Button } from '@/components/ui/button';
import { Instagram, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareToInstagram - Opens Instagram with caption copied to clipboard
 * Instagram doesn't have a web intent like Twitter, so we copy text and open the app
 */
const ShareToInstagram = ({ 
  caption, 
  buttonText = 'Share to Instagram',
  variant = 'default',
  size = 'default',
  className = ''
}) => {
  
  const handleShare = async () => {
    // Fallback copy method for secure contexts
    const copyToClipboard = async (text) => {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (err) {
          console.log('Clipboard API failed, trying fallback');
        }
      }
      
      // Fallback: Create temporary textarea
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        textArea.remove();
        return true;
      } catch (err) {
        console.error('Fallback copy failed:', err);
        textArea.remove();
        return false;
      }
    };

    const copied = await copyToClipboard(caption);
    
    if (copied) {
      toast.success('Caption copied! Paste it in Instagram', {
        description: 'Opening Instagram...',
        duration: 3000
      });
    } else {
      // Show caption in toast so user can copy manually
      toast.info('Copy this caption:', {
        description: caption.substring(0, 100) + '...',
        duration: 8000
      });
    }
    
    // Small delay then open Instagram
    setTimeout(() => {
      // Try to open Instagram app on mobile, fallback to web
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Try to open Instagram app
        window.location.href = 'instagram://camera';
        
        // Fallback to web after short delay if app doesn't open
        setTimeout(() => {
          window.open('https://www.instagram.com/', '_blank');
        }, 1500);
      } else {
        // Desktop - open Instagram web
        window.open('https://www.instagram.com/', '_blank');
      }
    }, 500);
  };

  return (
    <Button
      onClick={handleShare}
      variant={variant}
      size={size}
      className={`bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white ${className}`}
    >
      <Instagram className="w-4 h-4 mr-2" />
      {buttonText}
      <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
    </Button>
  );
};

/**
 * Quick share function - can be used anywhere without the component
 */
export const shareToInstagram = async (caption) => {
  // Fallback copy method
  const copyToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.log('Clipboard API failed');
      }
    }
    
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      textArea.remove();
      return true;
    } catch (err) {
      textArea.remove();
      return false;
    }
  };

  const copied = await copyToClipboard(caption);
  if (copied) {
    toast.success('Caption copied! Paste it in Instagram');
  } else {
    toast.info('Copy caption manually before sharing');
  }
  
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile) {
    window.location.href = 'instagram://camera';
  } else {
    window.open('https://www.instagram.com/', '_blank');
  }
};

export default ShareToInstagram;
